// src/components/PaywallView.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Check, ArrowLeft, Lock } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { t } from '../translations';
import { fetchOfferings, purchaseMonthlyPackage } from '../utils';

/**
 * PaywallView Component
 * Renders the subscription interface in English with a fixed display price of 25 SEK / month (incl. VAT).
 * Redesigned to match the clean, minimalist aesthetic of the login card.
 */
const PaywallView = ({ onPurchaseSuccess, onClose, isQuotaExceeded = false }) => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    async function loadOfferings() {
      try {
        await fetchOfferings();
      } catch (err) {
        console.error('Could not load offerings:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadOfferings();

    return () => {
      isMounted = false;
    };
  }, []);

  const handlePurchase = async () => {
    setPurchasing(true);
    setError(null);

    try {
      const started = await purchaseMonthlyPackage(null, language);
      
      if (!started) {
        setError(t(language, 'paywall_checkout_error'));
        setPurchasing(false);
      }
    } catch (err) {
      console.error('Purchase error in PaywallView:', err);
      setError(err.message || t(language, 'paywall_checkout_unexpected_error'));
      setPurchasing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[80vh] text-slate-400">
        <p className="text-sm font-medium animate-pulse">{t(language, 'paywall_loading')}</p>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-[85vh] px-4 py-8 bg-slate-50 font-sans">
      <div className="w-full max-w-md bg-white border border-slate-100 rounded-3xl p-8 sm:p-10 shadow-xl shadow-slate-200/60 relative text-center">
        
        {/* Back navigation button */}
        {onClose && (
          <button 
            onClick={onClose}
            className="absolute top-6 left-6 text-slate-400 hover:text-slate-700 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider transition-colors cursor-pointer"
          >
            <ArrowLeft size={15} /> <span>{t(language, 'paywall_back')}</span>
          </button>
        )}

        {/* Blue icon matching the LoginView header badge */}
        <div className="w-14 h-14 bg-blue-600 text-white rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg shadow-blue-500/25">
          <svg 
            className="w-7 h-7" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor" 
            strokeWidth={1.8}
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" 
            />
          </svg>
        </div>

        {/* Top status badge */}
        <div className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-600 px-3.5 py-1 rounded-full text-xs font-medium tracking-wide mb-3">
          {isQuotaExceeded ? <Lock size={13} className="text-amber-600" /> : <Sparkles size={13} className="text-blue-600" />} 
          <span>{isQuotaExceeded ? t(language, 'paywall_quota_reached_badge') : t(language, 'paywall_tier_basic')}</span>
        </div>

        {/* Classic italic serif title */}
        <h1 className="font-serif italic text-3xl text-slate-800 mb-2.5 tracking-tight">
          {isQuotaExceeded ? t(language, 'paywall_quota_reached_badge') : t(language, 'paywall_unlock_title')}
        </h1>

        {/* Subtitle */}
        <p className="text-slate-500 text-xs sm:text-sm mb-6 leading-relaxed max-w-sm mx-auto">
          {isQuotaExceeded 
            ? t(language, 'paywall_quota_desc')
            : t(language, 'paywall_trial_desc')}
        </p>

        {/* Pricing Box - Clean light version */}
        <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-5 mb-6 text-left relative">
          <div className="absolute top-3 right-3 bg-blue-50 text-blue-700 text-[10px] font-semibold uppercase px-2.5 py-0.5 rounded-md tracking-wider border border-blue-100">
            {t(language, 'paywall_standard_plan')}
          </div>

          <div className="font-semibold text-slate-800 text-sm mb-0.5">{t(language, 'paywall_monthly_sub')}</div>
          <div className="flex items-baseline gap-1.5 mb-3.5">
            <span className="text-2xl font-bold text-slate-900">{t(language, 'paywall_price_amount')}</span>
            <span className="text-slate-500 text-xs font-normal">{t(language, 'paywall_price_suffix')}</span>
          </div>

          <ul className="space-y-2 text-xs text-slate-600">
            <li className="flex items-start gap-2">
              <Check size={14} className="text-blue-600 shrink-0 mt-0.5" />
              <span>{t(language, 'paywall_feature_summaries')}</span>
            </li>
            <li className="flex items-start gap-2">
              <Check size={14} className="text-blue-600 shrink-0 mt-0.5" />
              <span>{t(language, 'paywall_feature_watches')}</span>
            </li>
            <li className="flex items-start gap-2">
              <Check size={14} className="text-blue-600 shrink-0 mt-0.5" />
              <span>{t(language, 'paywall_feature_saved')}</span>
            </li>
            <li className="flex items-start gap-2">
              <Check size={14} className="text-blue-600 shrink-0 mt-0.5" />
              <span>{t(language, 'paywall_feature_cancel')}</span>
            </li>
          </ul>
        </div>

        {/* Error message */}
        {error && (
          <div className="bg-red-50 border border-red-100 text-red-600 text-xs p-3 rounded-xl mb-4 text-left">
            {error}
          </div>
        )}

        {/* Action button - Styled like the Apple sign-in button */}
        <button
          onClick={handlePurchase}
          disabled={purchasing}
          className="w-full bg-slate-900 hover:bg-black text-white font-medium py-3.5 px-6 rounded-xl transition duration-150 ease-in-out shadow-sm flex items-center justify-center space-x-2 disabled:opacity-50 cursor-pointer"
        >
          {purchasing ? (
            <span className="text-sm">{t(language, 'paywall_opening_checkout')}</span>
          ) : (
            <span className="text-sm">
              {isQuotaExceeded ? t(language, 'paywall_btn_manage') : t(language, 'paywall_btn_subscribe')}
            </span>
          )}
        </button>

        {/* Bottom back button */}
        {onClose && (
          <button
            onClick={onClose}
            className="w-full mt-2.5 text-slate-400 hover:text-slate-600 text-xs font-medium py-1.5 transition-colors cursor-pointer"
          >
            {t(language, 'paywall_back_home')}
          </button>
        )}

        {/* Legal & Terms notice for Stripe compliance */}
        <div className="mt-6 pt-4 border-t border-slate-100 text-center">
          <p className="text-[11px] text-slate-400 mb-1.5">
            {t(language, 'paywall_renewal_notice')}
          </p>
          <p className="text-[11px] text-slate-400 leading-relaxed">
            {t(language, 'paywall_agree_notice')}
            <Link 
              to="/terms" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-slate-600 underline hover:text-slate-900 transition-colors"
            >
              {t(language, 'paywall_terms_link')}
            </Link>{' '}
            {t(language, 'paywall_and')}
            <Link 
              to="/privacy" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-slate-600 underline hover:text-slate-900 transition-colors"
            >
              {t(language, 'paywall_privacy_link')}
            </Link>.
          </p>
        </div>

      </div>
    </div>
  );
};

export default PaywallView;